import { useQuery } from '@apollo/client';
import {
  DEAL_PROMPT_US_REGION_CODE,
  type UseDealPromptDataPartnerOptions,
} from '@openx/components-deal-prompt/lib/utils/types.ts';
import type { Option } from '@openx/types/options.ts';
import { useMemo } from 'react';
import {
  GetDataPartnerOptionsDocument,
  type GetDataPartnerOptionsQuery,
  type GetDataPartnerOptionsQueryVariables,
} from '../../../graphql/query/audiences/GetDataPartnerOptions.generated.tsx';

export const useDealPromptDataPartnerOptions: UseDealPromptDataPartnerOptions = (region, countries, skip) => {
  const isNonUSRegion = Boolean(region) && region !== DEAL_PROMPT_US_REGION_CODE;

  // Build filter variables conditionally when region is defined
  const filterVariables = useMemo(() => {
    if (!isNonUSRegion) {
      return undefined;
    }

    const filter: Record<string, string | string[]> = {};

    if (region) {
      filter.region_codes = [region];
    }

    if (countries && countries.length > 0) {
      filter.country_codes = countries;
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [isNonUSRegion, region, countries]);

  const { data, loading } = useQuery<GetDataPartnerOptionsQuery, GetDataPartnerOptionsQueryVariables>(
    GetDataPartnerOptionsDocument,
    {
      skip: skip || !isNonUSRegion,
      variables: {
        filter: filterVariables,
      },
    }
  );

  const dataPartnerOptions = useMemo<Option[]>(
    () =>
      (data?.optionsByPath ?? [])
        .filter((option): option is NonNullable<typeof option> => option !== null)
        .map(option => ({ id: option.id, name: option.name })),
    [data?.optionsByPath]
  );

  return {
    loading: isNonUSRegion && loading,
    data: dataPartnerOptions,
  };
};
